<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { resolveLocale } from "./i18n";
import { useSession } from "./composables/useSession";
import { useReportFlow } from "./composables/useReportFlow";
import type { AppServices } from "./services/types";
import BrandHeader from "./components/BrandHeader.vue";
import ErrorNotice from "./components/ErrorNotice.vue";
import LoadingSpinner from "./components/LoadingSpinner.vue";
import ReportForm from "./components/ReportForm.vue";
import ReportResult from "./components/ReportResult.vue";

const props = defineProps<{ services: AppServices; mock?: boolean }>();
const { t, locale } = useI18n();
const {
  status,
  error: sessionError,
  context,
  initialize,
} = useSession(props.services, (value) => {
  locale.value = resolveLocale(value);
});
const { phase, error, result, deletion, deletionError, submit, retryDeletion } = useReportFlow(
  props.services,
  () => context.value,
);
const main = ref<HTMLElement | null>(null);
watch(
  locale,
  (value) => {
    document.documentElement.lang = value;
  },
  { immediate: true },
);
watch([status, phase], async () => {
  if (status.value === "initializing" || phase.value === "reading" || phase.value === "submitting")
    return;
  await nextTick();
  main.value?.focus();
});
onMounted(initialize);
</script>

<template>
  <div class="mx-auto flex min-h-dvh w-full max-w-lg flex-col p-6">
    <BrandHeader />
    <p v-if="mock" class="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
      {{ t("preview_notice") }}
    </p>
    <main ref="main" tabindex="-1" class="grow outline-none">
      <Transition name="screen" mode="out-in">
        <div
          v-if="status === 'initializing'"
          key="loading"
          class="flex items-center justify-center gap-3 py-12"
          role="status"
        >
          <LoadingSpinner class="size-10 text-indigo-500" />
          <span>{{ t("loading") }}</span>
        </div>
        <ErrorNotice
          v-else-if="sessionError"
          key="error"
          :error="sessionError"
          :action-label="t(sessionError.messageKey === 'error_auth' ? 'login' : 'retry')"
          @retry="initialize"
        />
        <ReportResult
          v-else-if="result"
          key="result"
          :result="result"
          :deletion="deletion"
          :error="deletionError"
          @retry-deletion="retryDeletion"
        />
        <ReportForm
          v-else-if="context"
          :key="context.message.id"
          :subject="context.message.subject"
          :sender="context.message.sender"
          :phase="phase"
          :error="error"
          @submit="submit"
        />
      </Transition>
    </main>
  </div>
</template>
